import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RichEditor } from "@/components/RichEditor";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, RefreshCcw, Reply, ChevronDown, ChevronUp, FileText, Send, Paperclip, Link2, X as XIcon } from "lucide-react";

function StatusBadge({ status }) {
  if (status === "sent") return <Badge className="bg-[#E7F0EB] text-[#2D4A3E] border-transparent"><CheckCircle2 className="w-3 h-3 mr-1" />Sent</Badge>;
  if (status === "failed") return <Badge className="bg-[#FCE8E6] text-[#C53030] border-transparent"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
  return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />{status}</Badge>;
}

const textFromHtml = (html) => {
  const d = document.createElement("div");
  d.innerHTML = html || "";
  return (d.textContent || "").trim();
};

const substituteHtml = (html, row, first) =>
  (html || "").replace(/\{\{company\}\}/gi, row.company_name || "").replace(/\{\{first_name\}\}/gi, first || "");

export default function History() {
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [checking, setChecking] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyRow, setReplyRow] = useState(null);
  const [replyTemplateId, setReplyTemplateId] = useState(null);
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [replyFile, setReplyFile] = useState(null);
  const [replyLink, setReplyLink] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => api.get("/applications").then((r) => setRows(r.data.applications || []));
  useEffect(() => {
    load();
    api.get("/reply-templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  }, []);

  const openReplyDialog = (row, tpl) => {
    const firstName = ""; // backend substitutes {{first_name}} from user profile
    const subj = substituteHtml(tpl?.subject || `Re: ${row.subject}`, row, firstName);
    const html = substituteHtml(tpl?.body_html || "", row, firstName);
    setReplyRow(row);
    setReplyTemplateId(tpl?.id || null);
    setReplySubject(subj);
    setReplyBody(html);
    setReplyFile(null);
    setReplyLink("");
    setReplyOpen(true);
  };

  const pickFile = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Attachment must be under 10 MB");
    setReplyFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  };

  const applyTemplateInDialog = (tpl) => {
    if (!replyRow) return;
    setReplyTemplateId(tpl?.id || null);
    setReplySubject(substituteHtml(tpl?.subject || `Re: ${replyRow.subject}`, replyRow, ""));
    setReplyBody(substituteHtml(tpl?.body_html || "", replyRow, ""));
  };

  const sendReply = async () => {
    if (!replyRow) return;
    if (!textFromHtml(replyBody)) return toast.error("Write a reply first");
    setSendingReply(true);
    try {
      const fd = new FormData();
      fd.append("subject", replySubject || "");
      fd.append("body_html", replyBody || "");
      fd.append("body_text", textFromHtml(replyBody));
      fd.append("portfolio_link", replyLink || "");
      if (replyTemplateId) fd.append("template_id", replyTemplateId);
      if (replyFile) fd.append("file", replyFile);
      await api.post(`/applications/${replyRow.id}/reply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Reply sent to ${replyRow.to_email}${replyFile ? " with attachment" : ""}`);
      setReplyOpen(false);
      setReplyRow(null);
      setReplyBody("");
      setReplyFile(null);
      setReplyLink("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSendingReply(false);
    }
  };

  const sent = rows.filter((r) => r.status === "sent").length;
  const failed = rows.filter((r) => r.status === "failed").length;
  const replied = rows.filter((r) => r.replied).length;

  const checkReplies = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/applications/check-replies");
      if (data.new_replies > 0) toast.success(`${data.new_replies} new ${data.new_replies === 1 ? "reply" : "replies"} found!`);
      else toast.info(`Checked ${data.checked} sent emails — no new replies yet.`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setChecking(false); }
  };

  return (
    <div className="flex min-h-screen bg-[#FDFDFB]">
      <Sidebar />
      <main className="flex-1 max-w-7xl mx-auto px-6 md:px-10 py-10 w-full">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline">History</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Every application on record</h1>
          </div>
          <Button onClick={checkReplies} disabled={checking} variant="outline" className="rounded-full border-[#E2E8E4]" data-testid="check-replies-btn">
            <RefreshCcw className={`w-3.5 h-3.5 mr-2 ${checking ? "animate-spin" : ""}`} />
            {checking ? "Checking inbox…" : "Check for replies"}
          </Button>
        </div>

        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className="p-6 border-[#E2E8E4]"><div className="overline">Total sent</div><div className="font-display text-3xl font-bold mt-2" data-testid="stats-sent">{sent}</div></Card>
          <Card className="p-6 border-[#E2E8E4]"><div className="overline">Failed</div><div className="font-display text-3xl font-bold mt-2 text-[#C53030]" data-testid="stats-failed">{failed}</div></Card>
          <Card className="p-6 border-[#E2E8E4]"><div className="overline">Replies</div><div className="font-display text-3xl font-bold mt-2 text-[#2D4A3E]" data-testid="stats-replies">{replied}</div></Card>
          <Card className="p-6 border-[#E2E8E4]"><div className="overline">Total attempts</div><div className="font-display text-3xl font-bold mt-2" data-testid="stats-total">{rows.length}</div></Card>
        </div>

        <Card className="border-[#E2E8E4] overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Company</TableHead>
              <TableHead>To</TableHead>
              <TableHead className="hidden md:table-cell">Subject</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden lg:table-cell">When</TableHead>
              <TableHead className="w-32 text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && (<TableRow><TableCell colSpan={6} className="text-center text-[#5C6661] py-10" data-testid="history-empty">No applications sent yet.</TableCell></TableRow>)}
              {rows.map((r) => (
                <>
                  <TableRow key={r.id} data-testid={`history-row-${r.id}`} className={r.replied ? "bg-[#E7F0EB]/25" : ""}>
                    <TableCell className="font-medium">{r.company_name}</TableCell>
                    <TableCell className="font-mono text-xs text-[#2D4A3E]">{r.to_email}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-[#5C6661]">{r.subject}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <StatusBadge status={r.status} />
                        {r.replied && <Badge className="bg-[#2D4A3E] text-white border-transparent" data-testid={`replied-badge-${r.id}`}><Reply className="w-3 h-3 mr-1" />Replied</Badge>}
                        {r.last_reply_sent_at && <Badge variant="secondary" className="text-[10px]" data-testid={`sent-reply-badge-${r.id}`}>You replied</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-[#5C6661]">{new Date(r.sent_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 justify-end">
                        {r.replied && (
                          <Button
                            size="sm"
                            onClick={() => openReplyDialog(r, templates[0] || null)}
                            className="h-7 rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50] text-xs px-3"
                            data-testid={`reply-open-${r.id}`}
                          >
                            <Reply className="w-3 h-3 mr-1" /> Reply
                          </Button>
                        )}
                        {r.replied && r.reply_snippet && (
                          <button data-testid={`expand-reply-${r.id}`} onClick={() => setExpandedId(expandedId === r.id ? null : r.id)} className="text-[#5C6661] hover:text-[#2D4A3E]">
                            {expandedId === r.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {expandedId === r.id && r.reply_snippet && (
                    <TableRow key={`${r.id}-snippet`}>
                      <TableCell colSpan={6} className="bg-[#F2F4F3] py-4">
                        <div className="overline mb-2">Reply preview</div>
                        <div className="text-sm whitespace-pre-wrap text-[#121413]" data-testid={`reply-snippet-${r.id}`}>{r.reply_snippet}</div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        </Card>
        <div className="mt-4 text-xs text-[#5C6661]">Tip: reply detection reads your inbox via IMAP using the same email account you send from. Gmail / Outlook / iCloud / Fastmail / Yahoo are supported.</div>
      </main>

      <Dialog open={replyOpen} onOpenChange={setReplyOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle>Reply to {replyRow?.company_name}</DialogTitle>
            <DialogDescription>
              Sends from your connected inbox, in-thread with the original message to <span className="font-mono text-[#2D4A3E]">{replyRow?.to_email}</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {templates.length > 0 && (
              <div>
                <Label className="overline">Load a template</Label>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {templates.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => applyTemplateInDialog(t)}
                      data-testid={`reply-tpl-pick-${t.id}`}
                      className={`text-xs rounded-full border px-3 py-1 transition-colors ${replyTemplateId === t.id ? "bg-[#2D4A3E] text-white border-transparent" : "border-[#E2E8E4] text-[#5C6661] hover:border-[#2D4A3E]/40 hover:text-[#121413]"}`}
                    >
                      <FileText className="w-3 h-3 inline mr-1" /> {t.name}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => applyTemplateInDialog(null)}
                    data-testid="reply-tpl-blank"
                    className="text-xs rounded-full border border-dashed border-[#E2E8E4] text-[#5C6661] hover:text-[#121413] px-3 py-1"
                  >Blank</button>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="reply-subject">Subject</Label>
              <Input id="reply-subject" data-testid="reply-subject" value={replySubject} onChange={(e) => setReplySubject(e.target.value)} className="mt-1.5 h-11 rounded-lg" />
            </div>

            <div>
              <Label>Message</Label>
              <div className="mt-1.5">
                <RichEditor value={replyBody} onChange={setReplyBody} placeholder="Type your reply — {{company}} and {{first_name}} are substituted automatically." data-testid="reply-body" />
              </div>
            </div>

            <div>
              <Label htmlFor="reply-link" className="flex items-center gap-1.5"><Link2 className="w-3 h-3 text-[#2D4A3E]" /> Portfolio link <span className="text-[#5C6661] font-normal">(optional)</span></Label>
              <Input
                id="reply-link"
                data-testid="reply-link"
                value={replyLink}
                onChange={(e) => setReplyLink(e.target.value)}
                placeholder="https://yourportfolio.com"
                className="mt-1.5 h-11 rounded-lg"
              />
            </div>

            <div>
              <Label className="flex items-center gap-1.5"><Paperclip className="w-3 h-3 text-[#2D4A3E]" /> Attachment <span className="text-[#5C6661] font-normal">(optional, up to 10 MB)</span></Label>
              {replyFile ? (
                <div className="mt-1.5 flex items-center justify-between gap-3 rounded-lg border border-[#E2E8E4] bg-[#E7F0EB]/30 px-4 py-3" data-testid="reply-file-chip">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-[#2D4A3E] shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-[#121413] truncate">{replyFile.name}</div>
                      <div className="text-xs text-[#5C6661]">{(replyFile.size / 1024).toFixed(1)} KB</div>
                    </div>
                  </div>
                  <button type="button" onClick={() => setReplyFile(null)} data-testid="reply-file-remove" className="text-[#5C6661] hover:text-[#C53030]" aria-label="Remove attachment">
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="reply-file-dropzone"
                  className={`mt-1.5 dropzone cursor-pointer border-2 border-dashed border-[#C7D6CC] rounded-lg px-5 py-6 text-center text-sm ${dragOver ? "drag-over" : ""}`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
                    data-testid="reply-file-input"
                    onChange={(e) => pickFile(e.target.files?.[0])}
                  />
                  <Paperclip className="w-5 h-5 mx-auto text-[#2D4A3E]" strokeWidth={1.5} />
                  <div className="mt-2 font-medium text-[#121413]">Drop a portfolio or reference file</div>
                  <div className="text-xs text-[#5C6661] mt-0.5">PDF, DOCX or image · attached to this reply only</div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyOpen(false)} className="rounded-full border-[#E2E8E4]" data-testid="reply-cancel">Cancel</Button>
            <Button onClick={sendReply} disabled={sendingReply} className="rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]" data-testid="reply-send">
              <Send className="w-4 h-4 mr-2" /> {sendingReply ? "Sending…" : "Send reply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
